export interface SqliteErrorInfo {
    name: string
    message: string
    stack?: string
    code?: number
    codeName?: string
}

interface MaybeSqliteError {
    name?: string
    message?: string
    stack?: string
    code?: number | string
    codeName?: string
    resultCode?: number
    resultCodeName?: string
}

export interface FailedCommitRecoveryResult {
    rollbackAttempted: boolean
    rollbackFailed: boolean
    rollbackError?: unknown
    shouldReopen: boolean
}

export function sqliteCodeNameFromCode(code: number): string {
    switch (code) {
        case 1:
            return 'SQLITE_ERROR'
        case 5:
            return 'SQLITE_BUSY'
        case 6:
            return 'SQLITE_LOCKED'
        case 8:
            return 'SQLITE_READONLY'
        case 10:
            return 'SQLITE_IOERR'
        case 13:
            return 'SQLITE_FULL'
        case 19:
            return 'SQLITE_CONSTRAINT'
        default:
            return `SQLITE_${code}`
    }
}

export function extractSqliteErrorInfo(error: unknown): SqliteErrorInfo {
    if (error && typeof error === 'object') {
        const maybeSqlite = error as MaybeSqliteError
        const message = maybeSqlite.message ?? String(error)
        const code =
            typeof maybeSqlite.resultCode === 'number'
                ? maybeSqlite.resultCode
                : typeof maybeSqlite.code === 'number'
                    ? maybeSqlite.code
                    : undefined
        const parsedCodeName = message.match(/\b(SQLITE_[A-Z0-9_]+)\b/)?.[1]
        const explicitCodeName =
            typeof maybeSqlite.resultCodeName === 'string'
                ? maybeSqlite.resultCodeName
                : typeof maybeSqlite.codeName === 'string'
                    ? maybeSqlite.codeName
                    : typeof maybeSqlite.code === 'string' && maybeSqlite.code.startsWith('SQLITE_')
                        ? maybeSqlite.code
                        : undefined
        return {
            name: maybeSqlite.name ?? 'Error',
            message,
            stack: maybeSqlite.stack,
            code,
            codeName: explicitCodeName ?? parsedCodeName ?? (code != null ? sqliteCodeNameFromCode(code) : undefined),
        }
    }

    return {
        name: 'Error',
        message: String(error),
    }
}

export function isBusyLikeSqliteError(error: unknown): boolean {
    const info = extractSqliteErrorInfo(error)
    if (info.code === 5 || info.code === 6) {
        return true
    }

    if (info.codeName === 'SQLITE_BUSY' || info.codeName === 'SQLITE_LOCKED') {
        return true
    }

    return /\bdatabase (?:is )?locked\b/i.test(info.message)
}

export function shouldReopenConnectionAfterSqliteFailure(error: unknown): boolean {
    const info = extractSqliteErrorInfo(error)
    return info.code === 10
        || info.code === 13
        || info.codeName === 'SQLITE_IOERR'
        || info.codeName === 'SQLITE_FULL'
}

export function shouldLogClientRequestFailureAsRecoverable(op: string, error: unknown): boolean {
    if (!shouldReopenConnectionAfterSqliteFailure(error)) {
        return false
    }

    return op === 'init-connection'
        || op === 'conn-execute'
        || op === 'tx-begin'
}

export function isNoActiveTransactionRollbackError(error: unknown): boolean {
    const info = extractSqliteErrorInfo(error)
    return /\b(?:cannot rollback|no transaction is active|no transaction)\b/i.test(info.message)
}

export async function recoverAfterFailedCommit(
    commitError: unknown,
    options: {
        execRollback: () => void | Promise<void>
        isAutocommit?: () => boolean | undefined
    },
): Promise<FailedCommitRecoveryResult> {
    const autocommit = options.isAutocommit?.()
    const shouldAttemptRollback = autocommit !== true

    if (!shouldAttemptRollback) {
        return {
            rollbackAttempted: false,
            rollbackFailed: false,
            shouldReopen: shouldReopenConnectionAfterSqliteFailure(commitError),
        }
    }

    try {
        await options.execRollback()
        return {
            rollbackAttempted: true,
            rollbackFailed: false,
            shouldReopen: shouldReopenConnectionAfterSqliteFailure(commitError),
        }
    } catch (rollbackError) {
        const rollbackStillLeavesHandleSuspect = !isNoActiveTransactionRollbackError(rollbackError)
        return {
            rollbackAttempted: true,
            rollbackFailed: rollbackStillLeavesHandleSuspect,
            rollbackError,
            shouldReopen: shouldReopenConnectionAfterSqliteFailure(commitError)
                || shouldReopenConnectionAfterSqliteFailure(rollbackError)
                || rollbackStillLeavesHandleSuspect,
        }
    }
}
